import { useEffect, useState } from 'react';
import { MARKER_ZONES, formatMinute, type TimePoint } from '@pipyar/core';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

export interface PointBlock {
  point: TimePoint;
  /** Stored times of bars that carried this point. */
  times: number[];
  /** Projected stored times ahead of the last candle. */
  futureTimes: number[];
  /** Next true UTC instant this point fires at, independent of candle data. */
  nextTime: number | null;
  /** Most recent UTC instant it fired at, bounding the current wait. */
  prevTime: number | null;
}

interface NextPointsProps {
  blocks: PointBlock[];
  /** The API's own clock when it answered, for skew correction. */
  serverTime?: number;
  hidden: Set<number>;
  onToggle: (id: number) => void;
}

/**
 * Time points ordered by how soon they fire, with a live countdown.
 *
 * The list is sorted by time remaining rather than by clock position, so the
 * next thing to happen is always at the top — the question this panel answers
 * is "what is coming", not "what does the day look like".
 */
export function NextPoints({ blocks, serverTime, hidden, onToggle }: NextPointsProps) {
  const { t } = useI18n();
  // One timer for the whole list. A countdown per row would mean a dozen
  // intervals all recomputing the same `now`.
  const [now, setNow] = useState(() => Date.now() / 1000);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, []);

  /*
   * Trust the server's clock over this machine's.
   *
   * Re-derived on every response, so it tracks the true offset rather than
   * assuming the browser was right when the page loaded. A desktop clock that
   * has drifted forwards would otherwise make every marker read as overdue.
   */
  const [skew, setSkew] = useState(0);
  useEffect(() => {
    if (serverTime !== undefined) setSkew(serverTime - Date.now() / 1000);
  }, [serverTime]);

  const rows = blocks
    .map((b) => {
      // `nextTime` is already a true UTC instant, so it needs no broker-shift
      // correction — and unlike the projected bar times it stays right when the
      // feed is behind, which it always is over a weekend.
      const secondsUntil = b.nextTime === null ? null : b.nextTime - (now + skew);
      return { block: b, secondsUntil };
    })
    // Anything without a projection sinks to the bottom rather than vanishing.
    .sort((a, b) => (a.secondsUntil ?? Infinity) - (b.secondsUntil ?? Infinity));

  /*
   * How far through the current wait we are, for the bar under the first row.
   *
   * The wait is bounded by the last point that fired and the next one due —
   * across ALL points, not just the leading one. Measuring against the leading
   * point's own previous occurrence would span a full day, so the bar would
   * creep imperceptibly and reset only once every 24 hours; bounding it by
   * whichever point fired most recently makes it restart at each event and
   * actually track the wait you are sitting through.
   */
  const leader = rows[0];
  const clockNow = now + skew;
  let progress: number | null = null;

  if (leader?.block.nextTime != null) {
    let start: number | null = null;
    for (const b of blocks) {
      if (b.prevTime != null && b.prevTime <= clockNow && (start === null || b.prevTime > start)) {
        start = b.prevTime;
      }
    }
    const span = start === null ? 0 : leader.block.nextTime - start;
    if (start !== null && span > 0) {
      progress = Math.min(1, Math.max(0, (clockNow - start) / span));
    }
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-surface/40 p-4 text-center text-sm text-muted-foreground">
        {t('points.none')}
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl p-3">
      <h2 className="mb-2.5 text-sm font-medium">{t('points.nextUp')}</h2>
      <ul className="space-y-1">
        {rows.map(({ block, secondsUntil }, index) => {
          const p = block.point;
          const visible = !hidden.has(-p.id);
          const imminent = secondsUntil !== null && secondsUntil <= 15 * 60 && secondsUntil > 0;

          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onToggle(-p.id)}
                className={cn(
                  'flex w-full items-start gap-2 rounded-lg px-1.5 py-1.5 text-start transition-colors',
                  'hover:bg-muted/60',
                  index === 0 && 'bg-primary/8',
                  !visible && 'opacity-50',
                )}
              >
                <span
                  className="mt-1.5 h-0 w-3 shrink-0 border-t-2 border-dashed"
                  style={{ borderColor: p.color }}
                  aria-hidden="true"
                />

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-medium">{p.name}</span>
                  <span className="block truncate text-[10px] text-muted-foreground">
                    {formatMinute(p.minute)}{' '}
                    {p.timezone
                      ? (MARKER_ZONES.find((z) => z.id === p.timezone)?.label ?? p.timezone)
                      : t('points.broker')}
                  </span>
                </span>

                <span
                  className={cn(
                    'tabular shrink-0 text-xs font-medium',
                    imminent ? 'text-primary' : 'text-muted-foreground',
                  )}
                >
                  {formatCountdown(secondsUntil)}
                </span>
              </button>

              {/* Only under the leading row: the bar measures the wait everyone
                  is in, so repeating it per point would say the same thing
                  nine times. */}
              {index === 0 && progress !== null && (
                <div
                  className="mx-1.5 mb-1 h-1 overflow-hidden rounded-full bg-muted"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(progress * 100)}
                  aria-label={t('points.nextUp')}
                >
                  <div
                    className={cn(
                      'h-full rounded-full transition-[width] duration-1000 ease-linear',
                      imminent ? 'bg-primary' : 'bg-primary/45',
                    )}
                    style={{ width: `${progress * 100}%` }}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <p className="mt-2 text-[10px] leading-snug text-muted-foreground">
        {t('points.hint')}
      </p>
    </div>
  );
}

/**
 * Countdown as "2h 14m" / "14m 03s".
 *
 * Seconds only appear under an hour: at six hours out the seconds digit changes
 * every tick and draws the eye for no reason, while under an hour it is exactly
 * what you want to watch.
 */
function formatCountdown(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds <= 0) return 'now';

  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}
